/* ─────────────────────────────────────────────────────────────
   KUNDA SHARED UI

   Small, dependency-free components that every screen needs.

   This file exists so App.js, longterm.js and requests.js can share
   ONE back-navigation implementation. They can't import from each other
   (App.js ⇄ longterm.js would be circular), which is exactly how three
   different back arrows drifted into the product. This module imports
   nothing from them, so anyone can import it safely.

   RULE: there is one KUNDA back arrow. It lives here. Do not draw
   another one anywhere else.
   ───────────────────────────────────────────────────────────── */

import React, { useState, useRef } from "react";

/* The KUNDA back arrow — line + arrowhead, stroke 2.2.
   NOT a bare chevron, and never a text "←". */
export function IconArrowLeft() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );
}

/* Standard secondary-page header: circular back button, centred title.
   Used by Terms, Privacy, Help, Account & Security — and now everywhere. */
export function SubpageHeader(props) {
  return (
    <div className="subpage-top">
      <button className="subpage-back" onClick={props.onBack} aria-label="Back">
        <IconArrowLeft/>
      </button>
      <h1 className="subpage-title">{props.title}</h1>
      <div className="subpage-spacer"/>
    </div>
  );
}

/* Back button on its own, for screens with a custom header layout
   (e.g. floating over a photo). Same icon, same size, same weight —
   only the positioning class differs. */
export function BackButton(props) {
  return (
    <button className={props.className || "subpage-back"}
            onClick={props.onBack} aria-label="Back">
      <IconArrowLeft/>
    </button>
  );
}

/* ── SHARED PROPERTY ICONS ──────────────────────────────────
   Short Stay and Long-Term are one platform. These are the icons for
   facts both flows describe (beds, baths, furnishing, property type).
   They live here so BOTH detail pages draw the same glyph at the same
   weight — governed by .detv2-quick-item svg { width:14px; height:14px }.

   Never substitute an emoji for these: an `svg {}` rule can't size an
   emoji, and different emoji glyphs render at different visual weights,
   which is exactly how the two rows drifted apart. */

export function IconBed() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"/>
      <path d="M2 10V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4"/>
      <path d="M12 10V8a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
      <line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  );
}

export function IconBath() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 14h18v2a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5v-2z"/>
      <path d="M3 14V8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6"/>
    </svg>
  );
}

/* Furnished — sofa */
export function IconSofa() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 12V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"/>
      <path d="M2 14a2 2 0 0 1 2-2 2 2 0 0 1 2 2v3h12v-3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v5H2z"/>
      <line x1="6" y1="19" x2="6" y2="21"/>
      <line x1="18" y1="19" x2="18" y2="21"/>
    </svg>
  );
}

/* Property type — building */
export function IconBuilding() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="4" y="3" width="16" height="18" rx="1.5"/>
      <line x1="9" y1="7" x2="9" y2="7.01"/>
      <line x1="15" y1="7" x2="15" y2="7.01"/>
      <line x1="9" y1="11" x2="9" y2="11.01"/>
      <line x1="15" y1="11" x2="15" y2="11.01"/>
      <path d="M10 21v-4h4v4"/>
    </svg>
  );
}


/* ── SHARED PHOTO CAROUSEL ──────────────────────────────────
   The swipeable card gallery. Lives here so Short Stay cards, Long-Term
   cards and detail heroes all use the SAME swipe physics, dots and
   thresholds. Reimplementing it per flow is how they drift apart. */

export var FALLBACK_IMG = "data:image/svg+xml," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 800">' +
  '<rect width="800" height="800" fill="#F2EDE5"/>' +
  '<g fill="none" stroke="#A89B8B" stroke-width="16" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M400 270 L570 415 L545 415 L545 555 L255 555 L255 415 L230 415 Z"/>' +
  '<path d="M355 555 L355 465 L445 465 L445 555"/>' +
  '</g></svg>'
);


function PhotoCarouselBase(props) {
  var photos = props.photos && props.photos.length > 0 ? props.photos : [FALLBACK_IMG];
  var [idx, setIdx] = useState(props.startIndex || 0);
  var [touchStart, setTouchStart] = useState(null);
  var [dragX, setDragX] = useState(0);
  var movedRef = useRef(false);
  var isLarge = !!props.large;

  function onTouchStart(e) {
    if (e) e.stopPropagation();
    movedRef.current = false;
    setTouchStart(e.touches[0].clientX);
    setDragX(0);
  }
  function onTouchMove(e) {
    if (touchStart === null) return;
    var diff = e.touches[0].clientX - touchStart;
    if (Math.abs(diff) > 8) movedRef.current = true;
    /* Resist past edges (rubber-band feel) */
    if ((idx === 0 && diff > 0) || (idx === photos.length - 1 && diff < 0)) {
      diff = diff * 0.35;
    }
    setDragX(diff);
  }
  function onTouchEnd(e) {
    if (touchStart === null) return;
    var diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && idx < photos.length - 1) setIdx(idx + 1);
      else if (diff < 0 && idx > 0) setIdx(idx - 1);
    }
    setTouchStart(null);
    setDragX(0);
  }
  /* Tap (not swipe) opens full-screen gallery on the large hero */
  function onClickCarousel() {
    if (!movedRef.current && props.onExpand) props.onExpand(idx);
  }

  function handleErr(e) { e.target.src = FALLBACK_IMG; }

  var trackStyle = {
    transform: "translateX(calc(" + (-idx * 100) + "% + " + dragX + "px))",
    transition: touchStart === null
      ? "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)"
      : "none"
  };

  return (
    <div className={"carousel " + (isLarge ? "carousel-lg" : "")}
         onTouchStart={onTouchStart}
         onTouchMove={onTouchMove}
         onTouchEnd={onTouchEnd}
         onClick={onClickCarousel}
         style={props.onExpand ? { cursor: "zoom-in" } : null}>
      <div className="car-track" style={trackStyle}>
        {photos.map(function(p, i) {
          var eager = props.eagerFirst && i === 0;
          return (
            <div className="car-slide" key={i}>
              <img src={p} alt=""
                   loading={eager ? "eager" : "lazy"}
                   decoding="async"
                   onError={handleErr} onLoad={function(e) { e.target.classList.add("img-loaded"); }}/>
            </div>
          );
        })}
      </div>

      {/* Image counter — top-right, only on large (detail) */}
      {isLarge && photos.length > 1 && (
        <div className="car-counter">
          {(idx + 1) + " / " + photos.length}
        </div>
      )}

      {/* Expand affordance — only on the large hero with an expand handler */}
      {isLarge && props.onExpand && photos.length > 1 && (
        <div className="car-viewall">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
          View photos
        </div>
      )}

      {/* Subtle dot indicators at bottom */}
      {photos.length > 1 && (
        <div className="car-dots">
          {photos.map(function(_, i) {
            return <span key={i} className={"car-dot " + (i === idx ? "on" : "")}/>;
          })}
        </div>
      )}
    </div>
  );
}

/* Memoized: re-renders only when the photo set or size changes. */
export var PhotoCarousel = React.memo(PhotoCarouselBase, function(a, b) {
  return a.photos === b.photos && a.large === b.large && a.startIndex === b.startIndex;
});

/* ── NOTIFICATION / STATUS LINE ICONS ───────────────────────
   Replace the coloured emoji that made the notification list read as
   basic. Monochrome, single stroke weight, sized by CSS at the call site.
   NOTE: the guest-home category illustrations (hut / key / pot) are brand
   artwork and are deliberately NOT part of this set. */

export function IconSend() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}
export function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}
export function IconPinLine() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  );
}
export function IconPhoneLine() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  );
}
export function IconCalendarLine() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
}
export function IconHomeLine() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/><path d="M9.5 21v-6h5v6"/>
    </svg>
  );
}
